import { useId } from 'react';

import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import CheckboxDropdownButton from '@cartwhl/ui/input/CheckboxDropdownButton';

import { useKeyPoseCaptureActions } from '../keyPoses/hooks/useKeyPoseCaptureActions';
import {
  useStore as useKeyPoseStore,
  useStoreApi as useKeyPoseStoreApi,
} from '../keyPoses/keyPoseStore';

interface ControlFrameSettingsDropdownProps {
  hasMotionToPlay: boolean;
}

export default function ControlFrameSettingsDropdown({
  hasMotionToPlay,
}: ControlFrameSettingsDropdownProps) {
  const baseId = useId();
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const { addIntervalKeyPoses } = useKeyPoseCaptureActions();
  const { autoGenerateControlFrames, showControlFrames } =
    keyPoseStore.state.controlFrameSettings;
  const controlFrameSettingsTooltip = autoGenerateControlFrames
    ? 'Disable auto-control motion'
    : 'Enable auto-control motion';

  return (
    <Tooltip text={controlFrameSettingsTooltip}>
      <span className="inline-flex">
        <CheckboxDropdownButton
          checked={autoGenerateControlFrames}
          aria-label={controlFrameSettingsTooltip}
          disabled={!hasMotionToPlay}
          dropdownAriaLabel="Auto-control motion settings"
          dropdownDisabled={!hasMotionToPlay}
          dropdownWidth={320}
          dropdownContent={
            <div className="flex flex-col px-2 py-4">
              <ControlFrameSettingDescription
                title="Auto-control motion"
                description="This adds 'control frames' to ensure the result of the edited motion is close to the original motion."
              />
              <div className="border-primary mt-3 flex flex-col gap-3 border-t pt-3">
                <ControlFrameSettingOption
                  id={`${baseId}-show`}
                  title="Show control frames"
                  description="Shows the auto-generated control frames as lighter orange keyframes on the timeline."
                  checked={showControlFrames}
                  disabled={!autoGenerateControlFrames}
                  onCheckedChange={(checked) => {
                    keyPoseStoreApi.setControlFrameSettings({
                      showControlFrames: checked,
                    });
                  }}
                />
              </div>
            </div>
          }
          onCheckedChange={(checked) => {
            keyPoseStoreApi.setControlFrameSettings({
              autoGenerateControlFrames: checked,
            });
            if (checked) {
              addIntervalKeyPoses();
            }
          }}
        >
          Auto-control motion
        </CheckboxDropdownButton>
      </span>
    </Tooltip>
  );
}

function ControlFrameSettingDescription({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="max-w-[20rem] text-left">
      <div className="ml-4 flex min-w-0 flex-col items-start gap-0.5">
        <Text as="span" variant="studio/body/medium/sm">
          {title}
        </Text>
        <Text
          as="span"
          variant="studio/body/sm"
          className="text-gray-45 font-normal"
        >
          {description}
        </Text>
      </div>
    </div>
  );
}

function ControlFrameSettingOption({
  id,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex max-w-[20rem] items-start gap-2 text-left">
      <Checkbox
        color="settings"
        id={id}
        variant="checkbox"
        align="start"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        setChecked={onCheckedChange}
      />
      <label
        htmlFor={id}
        className={`flex min-w-0 flex-col items-start gap-0.5 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
      >
        <Text as="span" variant="studio/body/medium/sm">
          {title}
        </Text>
        <Text
          as="span"
          variant="studio/body/sm"
          className="text-gray-45 font-normal"
        >
          {description}
        </Text>
      </label>
    </div>
  );
}
